import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, ExternalLink } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CobaltTriangle, OrangeArc, StripedTriangle } from "../components/Motifs";
import { IMG } from "../lib/site";

// 5 equal-weight sections. Each proof-point is a clickable link (placeholder "#" URLs — swap for real deliverables).
const categories = [
  {
    label: "Lifelong Learning & Credential Innovation",
    body: "Supporting states and institutions in reimagining how learners gain, demonstrate, and advance skills through flexible, workforce-aligned learning.",
    image: IMG.skydive,
    imgPos: "center 30%",
    highlights: [
      {
        title: "State credential frameworks: Utah",
        note: "work led in collaboration with NCEE",
        href: "https://schools.utah.gov/cte/firstcredentialprogram/_assets/FirstCredentialMasterPlanV1.1.pdf",
      },
      {
        title: "Microcredential ecosystem design: Texas Community College Learning Network",
        href: "https://dltx.highered.texas.gov/programs/texas-micro-credential-learning-network/",
      },
    ],
  },
  {
    label: "Effective Partnerships for Future-Ready Talent Pipelines",
    body: "Helping employers, community colleges, and regional partners build talent strategies that respond to today's workforce while preparing for tomorrow's economy.",
    image: IMG.group,
    imgPos: "center 30%",
    highlights: [
      {
        title: "An Actionable Framework for Regional Ecosystem Alignment",
        note: "work led while employed at the Education Design Lab",
        href: "https://eddesignlab.org/wp-content/uploads/2022/04/ANewRoleforHigherEd_EDL_Brief_V1May2022.pdf",
      },
      {
        title: "A Talent Roadmap for Economic Recovery in Hawai'i",
        note: "work led while employed at JFF",
        href: "https://static1.squarespace.com/static/640a4ca03eff8f1ba217a185/t/6761f9144d8ccc25dbeea4c5/1734474011671/HI-roadmap-Final-090220%2B%282%29%2B%281%29.pdf",
      },
      {
        title: "Supporting Displaced Hospitality Workers",
        note: "work led while employed at JFF",
        href: "https://jfforg-new-prod.s3.amazonaws.com/media/documents/JFF-Hilton_Report-Final1.pdf",
      },
    ],
  },
  {
    label: "Practitioner-Informed Policy & Systems Design",
    body: "Facilitating cross-sector collaboration that brings educators, employers, practitioners, and policymakers together to design policy that reflects lived experience.",
    image: IMG.speakingWood,
    imgPos: "center 15%",
    highlights: [
      {
        title: "National Center on Education and the Economy — Policy Levers",
        note: "work led while employed at NCEE",
        href: "https://ncee.org/policy-levers/",
      },
      {
        title: "Work-based learning policy recommendations",
        note: "work led while employed at JFF",
        href: "https://www.jff.org/idea/great-lakes-college-and-career-pathways-partnership/high-quality-work-based-learning-policy-recommendations/",
      },
    ],
  },
  {
    label: "Career Pathways & Postsecondary Success",
    body: "Designing learner-centered pathways that connect K-12, higher education, and industry through dual enrollment, work-based learning, and stackable credentials.",
    image: IMG.glacier,
    imgPos: "center 40%",
    highlights: [
      {
        title: "Promising Credentials",
        note: "work led while employed at JFF",
        href: "https://www.jff.org/wp-content/uploads/2023/09/GL_Rockford_HS_Promising_Credentials_FINAL.pdf",
      },
      {
        title: "Leadership and Governance in College and Career Pathways",
        href: "https://www.jff.org/idea/glccpp-leadership-governance-structures-and-commitments/",
      },
    ],
  },
  {
    label: "Global Perspectives, Local Impact",
    body: "Drawing inspiration from partnerships, fellowships, and learning experiences around the world to help communities design solutions that reflect their own strengths and aspirations.",
    image: IMG.morocco,
    imgPos: "center 30%",
    highlights: [
      {
        title: "Entrepreneurship and diplomacy fellowships in Tunisia and Egypt (video)",
        href: "https://www.youtube.com/watch?v=KQA_fdgUoKU",
      },
      {
        title: "Cleopatra to Coretta — East-West Dialogue action project",
        href: "https://www.eastwestdialogue.org/2015-action-project-cleopatra-to-coretta/",
      },
      { title: "Founder of Girls' Leadership, Organized Women (GLOW)", href: "#", hideBadge: true },
      {
        title: "Board Member, Hostelling International USA",
        href: "https://www.hiusa.org/about-us/board-of-directors",
      },
    ],
  },
];

const isExternal = (href) => href && /^https?:\/\//i.test(href);
const isPlaceholder = (href) => !href || href === "#";

const CategoryCard = ({ c, i }) => {
  const num = String(i + 1).padStart(2, "0");
  return (
    <Reveal delay={i * 0.05}>
      <article
        className="bg-cream border border-tan/70 grid md:grid-cols-12 overflow-hidden hover:border-cobalt/60 transition-colors duration-500"
        data-testid={`work-category-${i}`}
        id={`work-${i}`}
      >
        {/* Image tile */}
        <div className="md:col-span-4 relative aspect-[4/3] md:aspect-auto md:min-h-[320px] overflow-hidden">
          <img
            src={c.image}
            alt={c.label}
            className="w-full h-full object-cover transition-transform duration-[900ms] hover:scale-105"
            style={{ objectPosition: c.imgPos, filter: c.imgFilter }}
            loading="lazy"
            decoding="async"
          />
          <div className="absolute top-4 left-4 bg-cream text-cobalt font-display text-xl w-11 h-11 flex items-center justify-center border border-tan">
            {num}
          </div>
        </div>

        {/* Content */}
        <div className="md:col-span-8 p-8 lg:p-12 flex flex-col">
          <div className="overline text-orange-brand">Focus area · {num}</div>
          <h3 className="mt-3 font-display text-2xl lg:text-3xl text-ink leading-tight">{c.label}</h3>
          <p className="mt-4 text-ink-soft leading-relaxed">{c.body}</p>

          <div className="mt-6 pt-6 border-t border-tan/70">
            <div className="overline text-cobalt">Work samples</div>
            <ul className="mt-3 flex flex-wrap gap-2" data-testid={`work-highlights-${i}`}>
              {c.highlights.map((h, j) => {
                const placeholder = isPlaceholder(h.href);
                const external = !placeholder && isExternal(h.href);
                const Icon = external ? ExternalLink : FileText;

                if (placeholder) {
                  return (
                    <li key={h.title}>
                      <span
                        data-testid={`work-sample-${i}-${j}`}
                        title={h.hideBadge ? undefined : "Sample coming soon"}
                        className="inline-flex items-start gap-2 pl-2 pr-3 py-2 bg-cream-alt text-ink-soft text-sm border border-tan/70 cursor-default"
                      >
                        <span className="mt-0.5 w-7 h-7 flex items-center justify-center bg-cream border border-tan/70 text-ink-soft/60 flex-shrink-0">
                          <Icon size={13} strokeWidth={1.75} />
                        </span>
                        <span className="leading-snug">
                          {h.title}
                          {h.note && (
                            <span className="block text-[11px] italic text-ink-soft/70 mt-0.5">
                              {h.note}
                            </span>
                          )}
                        </span>
                        {!h.hideBadge && (
                          <span className="ml-1 mt-1 text-[10px] uppercase tracking-[0.15em] text-orange-brand font-medium flex-shrink-0">
                            Soon
                          </span>
                        )}
                      </span>
                    </li>
                  );
                }

                const commonProps = {
                  "data-testid": `work-sample-${i}-${j}`,
                  className:
                    "group inline-flex items-start gap-2 pl-2 pr-3 py-2 bg-cream-alt hover:bg-cobalt hover:text-cream text-ink text-sm border border-tan/70 hover:border-cobalt transition-colors",
                };
                const inner = (
                  <>
                    <span className="mt-0.5 w-7 h-7 flex items-center justify-center bg-cream border border-tan/70 group-hover:bg-orange-brand group-hover:text-cream group-hover:border-orange-brand transition-colors flex-shrink-0">
                      <Icon size={13} strokeWidth={1.75} />
                    </span>
                    <span className="leading-snug">
                      {h.title}
                      {h.note && (
                        <span className="block text-[11px] italic text-ink-soft/70 group-hover:text-cream/75 mt-0.5">
                          {h.note}
                        </span>
                      )}
                    </span>
                    <ArrowUpRight
                      size={14}
                      className="mt-1 opacity-40 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    />
                  </>
                );
                return (
                  <li key={h.title}>
                    {external ? (
                      <a href={h.href} target="_blank" rel="noopener noreferrer" {...commonProps}>
                        {inner}
                      </a>
                    ) : (
                      <a href={h.href} {...commonProps}>
                        {inner}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </article>
    </Reveal>
  );
};

const Work = () => {
  return (
    <div data-testid="work-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <OrangeArc className="absolute -top-10 -right-10 hidden md:block pointer-events-none" size={200} />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-12 lg:pt-16 pb-12 relative z-10">
          <Reveal>
            <div className="overline">Explorations in action</div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink max-w-4xl">
              From <em className="italic text-orange-brand">curiosity</em> to impact.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-lg text-ink-soft leading-relaxed max-w-3xl">
              For more than two decades, Leah has partnered with states, colleges, employers, foundations, and
              nonprofits to turn ambitious ideas into practical strategies that expand opportunity. Her work
              spans policy, practice, and partnership — always grounded in the belief that better systems are
              built together.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Focus areas — 5 equal cards */}
      <section className="pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid gap-6 lg:gap-8">
            {categories.map((c, i) => (
              <CategoryCard c={c} i={i} key={c.label} />
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 lg:py-24 relative overflow-hidden bg-cream-alt">
        <CobaltTriangle className="absolute -top-8 -left-8 opacity-90" size={120} flip />
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <p className="font-display italic text-3xl lg:text-4xl leading-tight text-ink">
              "Together, we're transforming big ideas into meaningful action."
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/collaborate"
              data-testid="work-cta"
              className="mt-10 inline-flex items-center gap-2 bg-ink text-cream px-8 py-4 text-sm tracking-[0.15em] uppercase hover:bg-cobalt transition-colors"
            >
              Explore together <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Work;
